import type { NativeSession } from "../native/session";
import type { SceneNode } from "../scene/model";
import { textData } from "../native/text";
import {
  textDeformations,
  type TextDeformation,
} from "../native/text-deformation";
import { measureTextNode } from "../render/native-text-layout";
import { originalIcon } from "./native-original-icons";
import {
  originalCompact,
  syncOriginalCompact,
} from "./native-original-controls";
import "./native-text-deformation.css";

export class NativeTextDeformation {
  private section = document.createElement("details");
  private type: HTMLSelectElement;
  private amount: HTMLInputElement;
  private exact: HTMLInputElement;
  private compact: HTMLElement;
  private original: SceneNode[] | null = null;
  private generation = -1;
  private sliderPointer: number | null = null;
  constructor(
    private session: NativeSession,
    panel: HTMLElement,
    private changed: () => void,
    private blocked: () => boolean,
    private prepare: () => void,
  ) {
    this.section.className = "native-properties native-text-deformation";
    this.section.innerHTML = `<summary>${originalIcon("deform")}Изгиб текста</summary><div class="native-property-body">
      <label>Форма<select id="text-deformation-type" aria-label="Изгиб текста"><option value="" disabled>Разные формы</option>${textDeformations.map(([id, label]) => `<option value="${id}">${label}</option>`).join("")}</select></label>
      <label>Кривизна<input id="text-deformation-amount" type="range" min="-100" max="100" step="1" value="50" aria-label="Кривизна текста"></label>
      <label>Значение<input id="text-deformation-exact" type="number" min="-100" max="100" step="1" aria-label="Значение кривизны текста"></label>
      <button id="text-deformation-reset" type="button">Сбросить изгиб</button>
    </div>`;
    panel.append(this.section);
    this.type = this.section.querySelector("select")!;
    this.amount = this.section.querySelector('input[type="range"]')!;
    this.exact = this.section.querySelector('input[type="number"]')!;
    this.type.onchange = () => {
      const preset = textDeformations.find(([id]) => id === this.type.value);
      if (preset)
        this.apply(() => ({ type: preset[0], amount: preset[2] }), true);
    };
    for (const field of [this.amount, this.exact]) {
      const apply = (commit: boolean) => {
        if (field.value.trim() === "") return;
        const amount = Number(field.value);
        if (!Number.isFinite(amount) || Math.abs(amount) > 100) {
          if (commit) this.render();
          return;
        }
        this.apply(
          (n) => ({ ...(textData(n).deformation ?? { type: "none" }), amount }),
          commit,
        );
      };
      field.oninput = () => apply(false);
      field.onchange = () => apply(true);
      field.onkeydown = (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          this.cancel();
          field.blur();
        }
      };
    }
    this.section.querySelector<HTMLButtonElement>("button")!.onclick = () =>
      this.apply(() => ({ type: "none", amount: 0 }), true);
    this.compact = originalCompact("native-text-deformation-compact", [
      { id: "text-deformation-type", icon: "deform", title: "Изгиб текста" },
      {
        id: "text-deformation-exact",
        icon: "smoothing",
        title: "Кривизна текста",
      },
    ]);
    // One contextual control keeps the original two-row phone property flow.
    const anchors = this.compact.querySelectorAll(".native-property-anchor"),
      popup = anchors[0]!.querySelector(".native-property-popup")!,
      amountPopup = anchors[1]!.querySelector(".native-property-popup")!,
      label = document.createElement("span");
    label.textContent = "Кривизна";
    popup.append(label, ...amountPopup.children);
    anchors[1]!.remove();
    const typeProxy = popup.querySelector<HTMLSelectElement>("select")!;
    typeProxy.onchange = () => {
      this.type.value = typeProxy.value;
      this.type.dispatchEvent(new Event("change", { bubbles: true }));
    };
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (
          this.original &&
          !this.section.contains(e.target as Node) &&
          !this.compact.contains(e.target as Node)
        )
          this.commit();
      },
      true,
    );
    for (const area of [this.section, this.compact]) {
      area.addEventListener(
        "pointerdown",
        (e) => {
          if ((e.target as HTMLElement).matches('input[type="range"]'))
            this.sliderPointer = e.pointerId;
        },
        true,
      );
      area.addEventListener("pointercancel", () => this.cancel());
      area.addEventListener("focusout", () => {
        if (this.original && this.sliderPointer === null) this.commit();
      });
    }
    document.addEventListener(
      "pointerup",
      (e) => {
        if (this.sliderPointer === e.pointerId) {
          this.sliderPointer = null;
          this.commit();
        }
      },
      true,
    );
    window.addEventListener("blur", () => this.cancel());
  }
  private nodes() {
    return this.session.selectedNodes.filter((n) => n.kind === "text");
  }
  private apply(value: (node: SceneNode) => TextDeformation, commit: boolean) {
    if (this.blocked() || (!this.original && this.session.gesturing)) return;
    this.prepare();
    if (!this.original) {
      const nodes = this.nodes();
      if (!nodes.length) return;
      if (
        nodes.every((n) => {
          const current = textData(n).deformation ?? {
              type: "none",
              amount: 0,
            },
            next = value(n);
          return current.type === next.type && current.amount === next.amount;
        })
      )
        return;
      this.original = nodes;
      this.generation = this.session.generation;
      this.session.begin();
    }
    this.session.replaceMany(
      this.original.map((n) =>
        measureTextNode(
          { ...n, text: { ...n.text, deformation: value(n) } },
          n,
        ),
      ),
    );
    if (commit && this.sliderPointer === null) this.commit();
    this.changed();
  }
  private commit() {
    if (!this.original) return;
    if (this.generation === this.session.generation) this.session.finish();
    this.original = null;
    this.changed();
  }
  private cancel() {
    this.sliderPointer = null;
    if (this.original && this.generation === this.session.generation)
      this.session.cancel();
    this.original = null;
    this.render();
    const node = this.nodes()[0];
    const amount = node ? (textData(node).deformation?.amount ?? 0) : 0;
    for (const field of [this.amount, this.exact]) {
      field.value = String(amount);
      for (const proxy of this.compact.querySelectorAll<HTMLInputElement>(
        `[data-original-source="${field.id}"]`,
      ))
        proxy.value = field.value;
    }
    this.changed();
  }
  render() {
    if (
      this.original &&
      (this.generation !== this.session.generation || !this.session.gesturing)
    )
      this.original = null;
    const nodes = this.nodes();
    this.section.hidden = this.compact.hidden = !nodes.length;
    if (!nodes.length) return;
    const values = nodes.map(
        (n) => textData(n).deformation ?? { type: "none", amount: 0 },
      ),
      first = values[0]!;
    const sameType = values.every((d) => d.type === first.type),
      sameAmount = values.every((d) => d.amount === first.amount);
    if (document.activeElement !== this.type)
      this.type.value = sameType ? first.type : "";
    if (document.activeElement !== this.amount)
      this.amount.value = String(first.amount);
    if (document.activeElement !== this.exact)
      this.exact.value = sameAmount ? String(first.amount) : "";
    this.exact.placeholder = sameAmount ? "" : "Разные";
    this.amount.disabled = this.exact.disabled = values.every(
      (d) => d.type === "none",
    );
    syncOriginalCompact(this.compact.id);
  }
}
